import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import type { AccessRequest, CommercialAccess, PlatformSupplierIntegration, SupportGrant } from '../../domain/models.js';
import { repository } from '../../repositories/index.js';
import { StatusBadge } from '../../components/StatusBadge.js';

function PageHeader({ eyebrow, title, description, action }: { eyebrow: string; title: string; description: string; action?: React.ReactNode }) {
  return <header className="page-header with-actions"><div><p className="eyebrow">{eyebrow}</p><h1>{title}</h1><p>{description}</p></div>{action}</header>;
}

export function TenantsPage() {
  const tenants: ReadonlyArray<readonly [string, string, string, string, string]> = [
    ['Мособои', 'ООО Мособои', '12 пользователей', 'Активная подписка', '4 поставщика'],
    ['WallPro', 'ООО ВоллПро', '4 пользователя', 'Пробный период', '2 поставщика'],
    ['Decor Hub', 'Декор Хаб', '18 пользователей', 'Доступ приостановлен', '6 поставщиков'],
    ['Тест 1', 'Тестовая компания', '0 пользователей', 'Отключён', '0 поставщиков'],
  ];
  return (
    <div className="page">
      <PageHeader eyebrow="ПЛАТФОРМА" title="Тенанты" description="Сводка платформенных объектов без доступа к каталогу, ценам и предложениям тенанта." />
      <section className="panel table-panel">
        <div className="data-table tenant-table">
          <div className="data-row data-header"><span>Тенант</span><span>Юридическое лицо</span><span>Пользователи</span><span>Доступ</span><span>Интеграции</span><span /></div>
          {tenants.map((tenant) => <div className="data-row" key={tenant[0]}><strong>{tenant[0]}</strong><span>{tenant[1]}</span><span>{tenant[2]}</span><span>{tenant[3]}</span><span>{tenant[4]}</span><button type="button" className="button small secondary">Открыть</button></div>)}
        </div>
      </section>
    </div>
  );
}

export function AccessRequestsPage() {
  const [items, setItems] = React.useState<AccessRequest[]>([]);
  React.useEffect(() => {
    repository.listAccessRequests().then(setItems);
  }, []);

  return (
    <div className="page" data-testid="access-requests-page">
      <PageHeader eyebrow="ПЛАТФОРМА · ДОСТУП" title="Запросы на доступ" description="Одобрение заявок сотрудников на роль внутри конкретного тенанта." />
      <section className="panel table-panel">
        <div className="data-table request-table">
          <div className="data-row data-header"><span>Тенант / компания</span><span>Сотрудник</span><span>Запрошенная роль</span><span>Заявитель</span><span>Дата запроса</span><span>Состояние</span><span /></div>
          {items.map((request) => (
            <div className="data-row" key={request.id} data-testid={`access-request-${request.id}`}>
              <span><strong>{request.tenant}</strong><small>{request.company}</small></span>
              <span><strong>{request.employeeName}</strong><small>{request.employeeEmail}</small></span>
              <span data-testid="requested-tenant-role">{request.requestedRole}</span>
              <span data-testid="access-request-requester">{request.requester}</span>
              <span>{request.requestedAt}</span>
              <span className={`request-state request-${request.state}`}>{request.stateLabel}</span>
              <span className="inline-actions">
                <button type="button" className="button small primary" disabled={request.state === 'approved'}>Подтвердить</button>
                <button type="button" className="button small ghost" disabled={request.state === 'rejected'}>Отклонить</button>
              </span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export function CommercialAccessPage() {
  const [items, setItems] = React.useState<CommercialAccess[]>([]);
  React.useEffect(() => {
    repository.listCommercialAccess().then(setItems);
  }, []);

  return (
    <div className="page" data-testid="commercial-access-page">
      <PageHeader eyebrow="ПЛАТФОРМА · ДОСТУП" title="Коммерческий доступ" description="Tenant-level entitlement без тарифов, checkout, оплаты и лицензирования по местам." />
      <section className="panel table-panel">
        <div className="data-table commercial-table">
          <div className="data-row data-header"><span>Тенант</span><span>Состояние</span><span>Начало</span><span>Окончание</span><span>Последнее изменение</span><span /></div>
          {items.map((item) => (
            <div className="data-row" key={item.id} data-commercial-state={item.state}>
              <strong>{item.tenant}</strong>
              <span className="commercial-state">{item.state}</span>
              <span>{item.effectiveFrom ?? '—'}</span>
              <span>{item.expiresAt ?? '—'}</span>
              <span>{item.lastChangedAt}</span>
              <button type="button" className={item.state === 'Доступ приостановлен' || item.state === 'Отключён' ? 'button small primary' : 'button small secondary'}>{item.administrativeAction}</button>
            </div>
          ))}
        </div>
      </section>
      <p className="page-footnote">Коммерческий доступ не изменяет tenant/platform границы и не открывает платформе операционные данные тенанта.</p>
    </div>
  );
}

export function IntegrationsPage() {
  const [items, setItems] = React.useState<PlatformSupplierIntegration[]>([]);
  const [selected, setSelected] = React.useState<PlatformSupplierIntegration | null>(null);

  React.useEffect(() => {
    repository.listPlatformSupplierIntegrations().then((value) => {
      setItems(value);
      setSelected(value[0] ?? null);
    });
  }, []);

  return (
    <div className="page" data-testid="platform-integrations-page" data-model="platform-supplier-integration">
      <PageHeader eyebrow="ПЛАТФОРМА · ИНТЕГРАЦИИ" title="Интеграции поставщиков" description="Платформенный реестр адаптеров и ingest-состояний. Tenant private prices, offers, секреты и учётные данные не отображаются." />
      <div className="master-detail">
        <section className="panel table-panel">
          <div className="data-table integration-table">
            <div className="data-row data-header"><span>Поставщик</span><span>Класс</span><span>Источник / метод</span><span>Здоровье</span><span>Последний ingest</span><span>Возможности</span><span>Версия</span><span /></div>
            {items.map((item) => (
              <div className={selected?.id === item.id ? 'data-row selected-row' : 'data-row'} key={item.id} data-integration-class={item.integrationClass}>
                <strong>{item.supplier}</strong>
                <span>{item.integrationClass}</span>
                <span>{item.sourceMethod}</span>
                <StatusBadge state={item.health} label={item.healthLabel} detail={`${item.healthDetail} Последний успешный ingest: ${item.lastSuccessfulIngest}.`} compact phoneCompact />
                <span>{item.lastSuccessfulIngest}</span>
                <span className="integration-capabilities">{item.capabilities.join(' · ')}</span>
                <span>{item.adapterVersion ?? item.feedVersion ?? '—'}</span>
                <button type="button" className="button small secondary" onClick={() => setSelected(item)}>Открыть</button>
              </div>
            ))}
          </div>
        </section>
        {selected ? (
          <aside className="panel integration-inspector" data-testid="integration-detail">
            <p className="eyebrow">ПЛАТФОРМЕННАЯ ИНТЕГРАЦИЯ</p>
            <h2>{selected.supplier}</h2>
            <StatusBadge state={selected.health} label={selected.healthLabel} detail={`${selected.healthDetail} Последний успешный ingest: ${selected.lastSuccessfulIngest}.`} />
            <dl className="definition-list">
              <div><dt>Класс интеграции</dt><dd>{selected.integrationClass}</dd></div>
              <div><dt>Источник / метод</dt><dd>{selected.sourceMethod}</dd></div>
              <div><dt>Последний успешный ingest</dt><dd>{selected.lastSuccessfulIngest}</dd></div>
              <div><dt>Версия адаптера</dt><dd>{selected.adapterVersion ?? 'Не указана'}</dd></div>
              <div><dt>Версия feed</dt><dd>{selected.feedVersion ?? 'Не указана'}</dd></div>
              {selected.fallbackReference ? <div><dt>Fallback/reference</dt><dd>{selected.fallbackReference}</dd></div> : null}
              <div><dt>Секреты</dt><dd>Не отображаются · серверная зона</dd></div>
            </dl>
            <h3>Возможности</h3>
            <div className="chip-list">
              {selected.capabilities.map((capability) => <span className="capability-chip neutral" key={capability}>{capability}</span>)}
            </div>
            <p className="panel-note">Заказы отображаются только как capability fact; действий заказа в этом интерфейсе нет.</p>
            <div className="button-row"><button type="button" className="button ghost">История ingest</button></div>
          </aside>
        ) : null}
      </div>
    </div>
  );
}

export function SupportAccessPage() {
  const [grants, setGrants] = React.useState<SupportGrant[]>([]);
  const navigate = useNavigate();
  React.useEffect(() => {
    repository.listSupportGrants().then(setGrants);
  }, []);

  return (
    <div className="page" data-testid="support-access-page">
      <PageHeader eyebrow="ПЛАТФОРМА · БЕЗОПАСНОСТЬ" title="Доступ поддержки" description="Явные гранты входа в read-only Support Mode. Платформенная личность сохраняется." />
      <section className="panel table-panel">
        <div className="data-table support-grant-table">
          <div className="data-row data-header"><span>Тенант</span><span>Оператор</span><span>Истекает</span><span>Состояние</span><span /></div>
          {grants.map((grant) => (
            <div className="data-row" key={grant.id}>
              <strong>{grant.tenant}</strong><span>{grant.operator}</span><span>{grant.expiresAt}</span>
              <span className={`grant-state grant-${grant.state}`}>{grant.state === 'active' ? 'Активен' : grant.state === 'expired' ? 'Истёк' : 'Отозван'}</span>
              {grant.state === 'active'
                ? <button type="button" className="button small primary" onClick={() => navigate('/support/catalog?query=R28003')}>Войти в Support Mode</button>
                : <button type="button" className="button small secondary" disabled>Недоступно</button>}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export function PlatformAccessPage() {
  const operators: ReadonlyArray<readonly [string, string, string, string, string]> = [
    ['Администратор платформы', 'platform.admin@datasota.example', 'Полный платформенный', 'MFA включена', 'Активен'],
    ['Поддержка DataSota', 'support@datasota.example', 'Support grants only', 'MFA включена', 'Активен'],
    ['Аудитор', 'audit@datasota.example', 'Чтение журнала', 'MFA включена', 'Неактивен'],
  ];
  return (
    <div className="page">
      <PageHeader eyebrow="ПЛАТФОРМА · БЕЗОПАСНОСТЬ" title="Доступ платформы" description="Платформенные роли не наследуют права владельца тенанта." action={<button type="button" className="button primary">Добавить оператора</button>} />
      <section className="panel table-panel">
        <div className="data-table platform-users-table">
          <div className="data-row data-header"><span>Оператор</span><span>Email</span><span>Область</span><span>MFA</span><span>Состояние</span><span /></div>
          {operators.map((operator) => <div className="data-row" key={operator[1]}><strong>{operator[0]}</strong><span>{operator[1]}</span><span>{operator[2]}</span><span>{operator[3]}</span><span>{operator[4]}</span><button type="button" className="button small secondary">Изменить</button></div>)}
        </div>
      </section>
    </div>
  );
}
